import { config } from '@config'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import { HttpService } from '@services/HttpService'
import { storage } from '@services/storageService'
import { ORDER_SERVICE_ENDPOINTS } from '../constants/apiConstants'
import { PRODUCT_SERVICE_ENDPOINTS } from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'

export default class OrderDataService {
  httpService = new HttpService()

  getOptionsHeader = () => {
    const accessToken: string = storage.getItem('access_token')
    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }
  }

  getCollectionHierarchy = (
    bsgId,
    orderId,
    orderType,
    filterData = [],
    searchKey = '',
    clientOrderId
  ) => {
    const orderPath = `/${orderType ?? OrderType.Client}Order/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCTS_HIERARCHY}/buyingSessionGroup/${bsgId}${orderPath}`
    const body = {
      filter: filterData
    }
    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader(),
        params: {
          ...(searchKey ? { searchKey } : {})
        }
      })
      .then(response => response.data?.data?.productHierarchy?.categories)
  }

  fetchCollectionProductsByHierarchy = (
    bsgId: string,
    orderId: string,
    orderType: string,
    filterData = {},
    searchKey = ''
  ) => {
    const orderPath = `/${orderType ?? OrderType.Client}Order/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_COLLECTION_PRODUCTS}/buyingSessionGroup/${bsgId}${orderPath}`
    const body = {
      filter: filterData
    }
    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader(),
        params: {
          ...(searchKey ? { searchKey } : {})
        }
      })
      .then(response => response.data?.data)
  }

  fetchCollectionFilterAttributes = (
    bsgId: string,
    orderId: string,
    orderType: OrderType
  ) => {
    const orderPath = `/${orderType ?? OrderType.Client}Order/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_COLLECTION_FILTER_ATTRIBUTES}/buyingSessionGroup/${bsgId}${orderPath}`
    return this.httpService
      .get(url, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data?.filterAttributes)
  }

  removeProductToOrder = async (
    orderId: string,
    buyingSessionProductId: string,
    orderType: OrderType,
    includeKPIs = false,
    includeDoorKPIs = false,
    filterData = {},
    searchKey = ''
  ) => {
    try {
      const urlParam = orderType === OrderType.Client ? 'clientOrder' : 'doorOrder'
      const url = `${config.buyingBoardServiceRestEndpoint}${
        ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS
      }/${urlParam}/${orderId}/buyingSessionProduct/${buyingSessionProductId}/${
        orderType === OrderType.Client ? 'remove' : 'unbook'
      }?includeKPIs=${includeKPIs}&includeDoorKPIs=${includeDoorKPIs}`

      const response = await this.httpService.post(
        url,
        {
          params: {
            ...(searchKey ? { searchKey } : {})
          },
          filter: filterData
        },
        {
          ...this.getOptionsHeader()
        }
      )
      return response.data?.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  addProductsToClientOrder = async (
    orderId: string,
    filterData: any,
    doorOrderIds: string[],
    searchKey: string
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS}/clientOrder/${orderId}`
    return this.httpService
      .post(
        url,
        {
          doorOrderIds,
          deleteProducts: false,
          isDoorOrder: false,
          filters: filterData
        },
        {
          ...this.getOptionsHeader(),
          params: { searchKey }
        }
      )
      .then(response => response.data?.data)
  }

  removeProductsFromClientOrder = (
    orderId: string,
    filterData = {},
    doorOrderIds,
    searchKey
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS}/clientOrder/${orderId}`
    return this.httpService
      .post(
        url,
        {
          doorOrderIds,
          deleteProducts: true,
          isDoorOrder: false,
          filters: filterData
        },
        {
          ...this.getOptionsHeader(),
          params: { searchKey }
        }
      )
      .then(response => response.data?.data)
  }

  addProductsToDoorOrder = (orderId: string, filterData = {}, doorOrderId, searchKey) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS}/clientOrder/${orderId}`
    return this.httpService
      .post(
        url,
        {
          doorOrderIds: [doorOrderId],
          deleteProducts: false,
          isDoorOrder: true,
          filters: filterData
        },
        {
          ...this.getOptionsHeader(),
          params: { searchKey }
        }
      )
      .then(response => response.data?.data)
  }

  removeProductsFromDoorOrder = (
    orderId: string,
    filterData = {},
    doorOrderId,
    searchKey
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS}/clientOrder/${orderId}`
    return this.httpService
      .post(
        url,
        {
          doorOrderIds: [doorOrderId],
          deleteProducts: true,
          isDoorOrder: true,
          filters: filterData
        },
        {
          ...this.getOptionsHeader(),
          params: { searchKey }
        }
      )
      .then(response => response.data?.data)
  }

  fetchAddProductsToOrderGroups = (bsgId, orderId, orderType, clientOrderId, body) => {
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/multiple-aggregates`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/multiple-aggregates`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${bsgId}${orderPath}`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response?.data)
  }

  fetchAddProductsToOrderByGrouping = (
    bsgId,
    orderId,
    orderType,
    clientOrderId,
    body
  ) => {
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/products`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/products`

    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${bsgId}${orderPath}`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response?.data)
  }

  addProductsToEnhancedClientOrder = async (
    orderId: string,
    buyingSessionGroupId,
    body: any
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}/clientOrder/${orderId}/add-all-products`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }

  addProductsToEnhancedDoorOrder = (
    orderId: string,
    buyingSessionGroupId,
    doorOrderId,
    body: any
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}/clientOrder/${orderId}/doorOrder/${doorOrderId}/add-all-products`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }

  removeAllProductsFromEnhancedClientOrder = (
    orderId: string,
    buyingSessionGroupId,
    body = {}
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}/clientOrder/${orderId}/remove-all-products`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }

  removeAllProductsFromEnhancedDoorOrder = (
    orderId: string,
    buyingSessionGroupId,
    doorOrderId,
    body: any
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}/clientOrder/${orderId}/doorOrder/${doorOrderId}/remove-all-products`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }

  addProductToEnhancedOrder = (
    productId: string,
    orderId: string,
    buyingSessionGroupId,
    clientOrderId = '',
    body: any,
    orderType: string
  ) => {
    const urlSuffix =
      orderType === OrderType.Door
        ? `/clientOrder/${clientOrderId}/doorOrder/${orderId}/buyingSessionProduct/${productId}`
        : `/clientOrder/${orderId}/buyingSessionProduct/${productId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}${urlSuffix}`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }

  removeProductFromEnhancedOrder = (
    productId: string,
    orderId: string,
    buyingSessionGroupId,
    clientOrderId = '',
    body: any,
    orderType: string
  ) => {
    const urlSuffix =
      orderType === OrderType.Door
        ? `/clientOrder/${clientOrderId}/doorOrder/${orderId}/buyingSessionProduct/${productId}/remove`
        : `/clientOrder/${orderId}/buyingSessionProduct/${productId}/remove`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}${urlSuffix}`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data?.data)
  }
}
